// /app/frontend/src/pages/MyOrdersPage.jsx
// Order history page with reorder functionality

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Truck, CheckCircle, Clock, ShoppingBag,
  Loader2, ChevronDown, ChevronUp, RefreshCw, FileText,
  ArrowLeft, Calendar, DollarSign, MapPin, Eye, Wrench, Download, Trash2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import InstallationScheduler from '../components/InstallationScheduler';
import OrderTimeline from '../components/OrderTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800', step: 1 },
  processing: { label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-800', step: 2 },
  shipped: { label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-800', step: 3 },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-800', step: 4 },
  cancelled: { label: 'Cancelled', icon: Package, color: 'bg-red-100 text-red-800', step: 0 }
};

const CARRIER_TRACKING_URLS = {
  'USPS': 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  'UPS': 'https://www.ups.com/track?tracknum=',
  'FedEx': 'https://www.fedex.com/fedextrack/?trknbr=',
  'DHL': 'https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id='
};

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  const { addToCart } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderingId, setReorderingId] = useState(null);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'quotes'
  const [installationOrder, setInstallationOrder] = useState(null); // Order to schedule installation for
  const [viewQuote, setViewQuote] = useState(null); // Quote to view details
  const [cancellingQuoteId, setCancellingQuoteId] = useState(null);
  const [downloadingQuoteId, setDownloadingQuoteId] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Fetch orders and quotes separately
      const [ordersRes, quotesRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.warn('Failed to fetch quotes:', err.message);
          return { data: [] };
        })
      ]);
      
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : ordersRes.data?.orders || []);
      setQuotes(Array.isArray(quotesRes.data) ? quotesRes.data : quotesRes.data?.quotes || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load order history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/my-orders');
      return;
    }
    fetchOrders();
  }, [authLoading, isAuthenticated, navigate, fetchOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    const baseUrl = CARRIER_TRACKING_URLS[carrier] || CARRIER_TRACKING_URLS['USPS'];
    return `${baseUrl}${trackingNumber}`;
  };

  const handleReorder = async (order) => {
    setReorderingId(order.id);
    try {
      // Get items from the order
      const items = order.items || order.line_items || [];
      
      if (items.length === 0) {
        toast.error('No items found in this order');
        return;
      }

      // Add each item to cart
      for (const item of items) {
        const config = {
          product_type: item.product_type || 'roller-shade',
          width: item.width,
          height: item.height,
          fabric_brand: item.fabric_brand,
          fabric_collection: item.fabric_collection,
          fabric_material: item.fabric_material,
          fabric_color: item.fabric_color,
          fabric_color_name: item.fabric_color_name,
          mount_type: item.mount_type || 'inside',
          control_type: item.control_type || 'cordless',
          control_side: item.control_side || 'right',
          roll_direction: item.roll_direction || 'standard',
          quantity: item.quantity || 1,
          room_name: item.room_name || 'Reorder'
        };
        
        await addToCart(config);
      }
      
      toast.success(`${items.length} item(s) added to cart!`);
      // Navigate to cart to review before checkout
      navigate('/cart');
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to add items to cart');
    } finally {
      setReorderingId(null);
    }
  };

  const handleQuoteToOrder = async (quote) => {
    setReorderingId(quote.id);
    try {
      const items = quote.items || quote.line_items || [];
      
      if (items.length === 0) {
        toast.error('No items found in this quote');
        return;
      }

      for (const item of items) {
        const config = {
          product_type: item.product_type || 'roller-shade',
          width: item.width,
          height: item.height,
          fabric_brand: item.fabric_brand,
          fabric_collection: item.fabric_collection,
          fabric_material: item.fabric_material,
          fabric_color: item.fabric_color,
          fabric_color_name: item.fabric_color_name,
          mount_type: item.mount_type || 'inside',
          control_type: item.control_type || 'cordless',
          control_side: item.control_side || 'right',
          roll_direction: item.roll_direction || 'standard',
          quantity: item.quantity || 1,
          room_name: item.room_name || 'From Quote'
        };
        
        await addToCart(config);
      }
      
      toast.success('Quote items added to cart!');
      navigate('/cart');
    } catch (error) {
      console.error('Failed to convert quote:', error);
      toast.error('Failed to add items to cart');
    } finally {
      setReorderingId(null);
    }
  };

  // Cancel/Delete a quote (soft delete)
  const handleCancelQuote = async (quote) => {
    if (!window.confirm(`Are you sure you want to cancel quote ${quote.quote_number || quote.id?.slice(0, 8)}? This cannot be undone.`)) {
      return;
    }
    
    setCancellingQuoteId(quote.id);
    try {
      await axios.put(`${API_URL}/api/quotes/${quote.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote cancelled successfully');
      // Remove from local list
      setQuotes(prev => prev.filter(q => q.id !== quote.id));
    } catch (error) {
      console.error('Failed to cancel quote:', error);
      toast.error('Failed to cancel quote');
    } finally {
      setCancellingQuoteId(null);
    }
  };

  // Download quote as PDF
  const handleDownloadQuotePDF = async (quote) => {
    setDownloadingQuoteId(quote.id);
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${quote.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-${quote.quote_number || quote.id?.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Quote PDF downloaded');
    } catch (error) {
      console.error('Failed to download quote PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingQuoteId(null);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-600" />
            <p className="mt-4 text-neutral-500">Loading your orders...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-3 mb-2">
              <ShoppingBag className="w-8 h-8 text-amber-600" />
              <h1 className="text-3xl font-bold text-neutral-900">My Orders</h1>
            </div>
            <p className="text-neutral-500">
              View your order history and easily reorder past purchases.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'orders' ? 'default' : 'outline'}
              onClick={() => setActiveTab('orders')}
              className={activeTab === 'orders' ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Orders ({orders.length})
            </Button>
            <Button
              variant={activeTab === 'quotes' ? 'default' : 'outline'}
              onClick={() => setActiveTab('quotes')}
              className={activeTab === 'quotes' ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              <FileText className="w-4 h-4 mr-2" />
              Saved Quotes ({quotes.length})
            </Button>
          </div>

          {/* Orders List */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              {orders.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                  <ShoppingBag className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-700 mb-2">
                    No Orders Yet
                  </h2>
                  <p className="text-neutral-500 mb-6">
                    You haven't placed any orders yet. Start shopping!
                  </p>
                  <Button
                    onClick={() => navigate('/products/roller-shades')}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Shop Now
                  </Button>
                </div>
              ) : (
                <AnimatePresence>
                  {orders.map((order, index) => {
                    const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
                      >
                        {/* Order Header */}
                        <div className="p-4 border-b border-neutral-100 bg-neutral-50">
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="text-sm text-neutral-500">Order #</p>
                                <p className="font-semibold text-neutral-900">{order.order_number || order.id?.slice(0, 8)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-neutral-500">Date</p>
                                <p className="font-medium text-neutral-700">{formatDate(order.created_at)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-neutral-500">Total</p>
                                <p className="font-semibold text-neutral-900">{formatPrice(order.total)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setInstallationOrder(order)}
                                className="gap-1"
                                title="Schedule Installation"
                              >
                                <Wrench className="w-4 h-4" />
                                <span className="hidden sm:inline">Install</span>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleReorder(order)}
                                disabled={reorderingId === order.id}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                {reorderingId === order.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Reorder
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Tracking Info */}
                        {order.tracking_number && (
                          <div className="px-4 py-3 border-b border-neutral-100 bg-purple-50 flex items-center justify-between">
                            <div>
                              <p className="text-sm text-purple-700 font-medium">Tracking: {order.tracking_number}</p>
                              <p className="text-xs text-purple-600">{order.carrier || 'USPS'}</p>
                            </div>
                            <a
                              href={getTrackingUrl(order.carrier || 'USPS', order.tracking_number)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                            >
                              <Truck className="w-4 h-4" />
                              Track Package
                            </a>
                          </div>
                        )}

                        {/* Order Items Accordion */}
                        <Accordion type="single" collapsible>
                          <AccordionItem value="items" className="border-none">
                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-neutral-50">
                              <span className="flex items-center gap-2 text-sm">
                                <Eye className="w-4 h-4" />
                                View {(order.items || order.line_items || []).length} Item(s)
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-3">
                                {(order.items || order.line_items || []).map((item, itemIndex) => (
                                  <div
                                    key={itemIndex}
                                    className="flex items-start gap-4 p-3 bg-neutral-50 rounded-lg"
                                  >
                                    {/* Color Swatch */}
                                    <div
                                      className="w-16 h-16 rounded-lg border border-neutral-200 flex-shrink-0"
                                      style={{ backgroundColor: item.fabric_color_hex || '#E5E5E5' }}
                                    >
                                      {item.fabric_image_url && (
                                        <img
                                          src={item.fabric_image_url}
                                          alt={item.fabric_color_name}
                                          className="w-full h-full object-cover rounded-lg"
                                        />
                                      )}
                                    </div>
                                    
                                    {/* Item Details */}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-neutral-900">
                                        {item.product_type === 'roller-shade' ? 'Roller Shade' : item.product_type}
                                      </p>
                                      <p className="text-sm text-neutral-500">
                                        {item.fabric_brand} • {item.fabric_color_name || 'Custom'}
                                      </p>
                                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-neutral-400">
                                        <span>{item.width}" × {item.height}"</span>
                                        <span>{item.mount_type} mount</span>
                                        <span>{item.control_type}</span>
                                        {item.room_name && <span>Room: {item.room_name}</span>}
                                      </div>
                                    </div>
                                    
                                    {/* Price & Qty */}
                                    <div className="text-right">
                                      <p className="font-semibold text-neutral-900">{formatPrice(item.price || item.unit_price)}</p>
                                      <p className="text-sm text-neutral-500">Qty: {item.quantity || 1}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Shipping Address */}
                              {order.shipping_address && (
                                <div className="mt-4 p-3 bg-neutral-50 rounded-lg">
                                  <p className="text-sm font-medium text-neutral-700 flex items-center gap-2 mb-1">
                                    <MapPin className="w-4 h-4" />
                                    Shipping Address
                                  </p>
                                  <p className="text-sm text-neutral-500">
                                    {order.shipping_address.street}<br />
                                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                                  </p>
                                </div>
                              )}

                              {/* Order Timeline */}
                              <div className="mt-4 p-4 bg-white rounded-lg border border-neutral-200">
                                <p className="text-sm font-medium text-neutral-700 mb-3">Order Progress</p>
                                <OrderTimeline
                                  currentStatus={order.order_status || order.status || 'pending'}
                                  statusHistory={order.status_history}
                                  trackingNumber={order.tracking_number}
                                  carrier={order.shipping_carrier}
                                  compact={true}
                                />
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          )}

          {/* Quotes List */}
          {activeTab === 'quotes' && (
            <div className="space-y-4">
              {quotes.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                  <FileText className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-700 mb-2">
                    No Saved Quotes
                  </h2>
                  <p className="text-neutral-500 mb-6">
                    Save your configurations as quotes to review later.
                  </p>
                  <Button
                    onClick={() => navigate('/products/roller-shades')}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Create Quote
                  </Button>
                </div>
              ) : (
                <AnimatePresence>
                  {quotes.map((quote, index) => (
                    <motion.div
                      key={quote.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`bg-white rounded-xl border overflow-hidden ${quote.status === 'cancelled' ? 'border-red-200 opacity-60' : 'border-neutral-200'}`}
                    >
                      <div className="p-4 border-b border-neutral-100 bg-neutral-50">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-sm text-neutral-500">Quote #</p>
                              <p className="font-semibold text-neutral-900">{quote.quote_number || quote.id?.slice(0, 8)}</p>
                            </div>
                            {quote.quote_name && (
                              <div>
                                <p className="text-sm text-neutral-500">Name</p>
                                <p className="font-medium text-neutral-700">{quote.quote_name}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-sm text-neutral-500">Created</p>
                              <p className="font-medium text-neutral-700">{formatDate(quote.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-neutral-500">Total</p>
                              <p className="font-semibold text-neutral-900">{formatPrice(quote.total)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {quote.status === 'cancelled' ? (
                              <Badge className="bg-red-100 text-red-800">
                                Cancelled
                              </Badge>
                            ) : (
                              <>
                                {quote.expires_at && new Date(quote.expires_at) > new Date() && (
                                  <Badge className="bg-green-100 text-green-800">
                                    Valid until {formatDate(quote.expires_at)}
                                  </Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setViewQuote(quote)}
                                  title="View Quote Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadQuotePDF(quote)}
                                  disabled={downloadingQuoteId === quote.id}
                                  title="Download PDF"
                                >
                                  {downloadingQuoteId === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCancelQuote(quote)}
                                  disabled={cancellingQuoteId === quote.id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Cancel Quote"
                                >
                                  {cancellingQuoteId === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleQuoteToOrder(quote)}
                                  disabled={reorderingId === quote.id}
                                  className="bg-amber-600 hover:bg-amber-700"
                                >
                                  {reorderingId === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <ShoppingBag className="w-4 h-4 mr-1" />
                                      Order Now
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Quote Items */}
                      <div className="p-4">
                        <p className="text-sm text-neutral-500 mb-3">
                          {(quote.items || quote.line_items || []).length} item(s)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(quote.items || quote.line_items || []).slice(0, 4).map((item, i) => (
                            <div
                              key={i}
                              className="w-12 h-12 rounded border border-neutral-200"
                              style={{ backgroundColor: item.fabric_color_hex || '#E5E5E5' }}
                              title={`${item.width}" × ${item.height}" - ${item.fabric_color_name || 'Custom'}`}
                            />
                          ))}
                          {(quote.items || quote.line_items || []).length > 4 && (
                            <div className="w-12 h-12 rounded bg-neutral-100 flex items-center justify-center text-sm text-neutral-500">
                              +{(quote.items || quote.line_items || []).length - 4}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          )}
        </div>
      </main>
      
      <Footer />

      {/* Installation Scheduler Dialog */}
      <Dialog open={!!installationOrder} onOpenChange={(open) => !open && setInstallationOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Installation</DialogTitle>
          </DialogHeader>
          {installationOrder && (
            <InstallationScheduler
              orderId={installationOrder.id}
              token={token}
              shippingAddress={installationOrder.shipping_address || {}}
              onComplete={() => {
                setInstallationOrder(null);
                toast.success('Installation request submitted!');
              }}
              onSkip={() => setInstallationOrder(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Details Dialog */}
      <Dialog open={!!viewQuote} onOpenChange={(open) => !open && setViewQuote(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Quote Details</span>
              <span className="text-sm font-normal text-neutral-500">
                #{viewQuote?.quote_number || viewQuote?.id?.slice(0, 8)}
              </span>
            </DialogTitle>
          </DialogHeader>
          {viewQuote && (
            <div className="space-y-6">
              {/* Quote Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-neutral-50 rounded-lg">
                <div>
                  <p className="text-sm text-neutral-500">Created</p>
                  <p className="font-medium">{formatDate(viewQuote.created_at)}</p>
                </div>
                {viewQuote.quote_name && (
                  <div>
                    <p className="text-sm text-neutral-500">Quote Name</p>
                    <p className="font-medium">{viewQuote.quote_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-neutral-500">Subtotal</p>
                  <p className="font-medium">{formatPrice(viewQuote.subtotal)}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Tax</p>
                  <p className="font-medium">{formatPrice(viewQuote.tax)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-neutral-500">Total</p>
                  <p className="text-xl font-bold text-amber-600">{formatPrice(viewQuote.total)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h4 className="font-medium mb-2">Customer Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="text-neutral-500">Name:</span> {viewQuote.customer_name}</p>
                  <p><span className="text-neutral-500">Email:</span> {viewQuote.customer_email}</p>
                  {viewQuote.customer_phone && (
                    <p><span className="text-neutral-500">Phone:</span> {viewQuote.customer_phone}</p>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium mb-3">Items ({(viewQuote.items || viewQuote.line_items || []).length})</h4>
                <div className="space-y-3">
                  {(viewQuote.items || viewQuote.line_items || []).map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
                      <div 
                        className="w-12 h-12 rounded border border-neutral-200 flex-shrink-0"
                        style={{ backgroundColor: item.fabric_color_hex || '#E5E5E5' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{item.fabric_color_name || 'Custom'}</p>
                        <p className="text-sm text-neutral-500">
                          {item.width}" × {item.height}" • {item.mount_type} mount • {item.control_type}
                        </p>
                        {item.room_name && (
                          <p className="text-xs text-neutral-400">{item.room_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(item.price || item.total)}</p>
                        {item.quantity > 1 && (
                          <p className="text-sm text-neutral-500">Qty: {item.quantity}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadQuotePDF(viewQuote)}
                  disabled={downloadingQuoteId === viewQuote.id}
                >
                  {downloadingQuoteId === viewQuote.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
                <Button
                  onClick={() => {
                    handleQuoteToOrder(viewQuote);
                    setViewQuote(null);
                  }}
                  disabled={reorderingId === viewQuote.id}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {reorderingId === viewQuote.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ShoppingBag className="w-4 h-4 mr-2" />
                  )}
                  Order Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOrdersPage;
